/* Shared Leaflet map chrome: basemap style toggle (Light/Dark/Satellite/Hybrid)
   and a "heading up" frame that rotates the map to the device's direction of
   travel while keeping markers, popups and controls upright. */
import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import { Sun, Moon, Satellite, Layers, Compass } from "lucide-react";

export const TILE_LAYERS = {
  light: {
    label: "Light", Icon: Sun,
    url: "https://mt{s}.google.com/vt/lyrs=r&x={x}&y={y}&z={z}&hl=en",
    subdomains: ["0", "1", "2", "3"],
    attr: "Map data &copy; Google",
    bg: "#e9e9ea",
  },
  dark: {
    label: "Dark", Icon: Moon,
    url: "https://mt{s}.google.com/vt/lyrs=r&x={x}&y={y}&z={z}&hl=en",
    subdomains: ["0", "1", "2", "3"],
    attr: "Map data &copy; Google",
    bg: "#0a0a0b",
    // Night mode is the same road tiles run through a colour inversion, so it
    // needs no second tile provider or API key.
    invert: true,
  },
  satellite: {
    label: "Satellite", Icon: Satellite,
    url: "https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}&hl=en",
    subdomains: ["0", "1", "2", "3"],
    attr: "Imagery &copy; Google",
    bg: "#0a0a0b",
  },
  hybrid: {
    label: "Hybrid", Icon: Layers,
    url: "https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&hl=en",
    subdomains: ["0", "1", "2", "3"],
    attr: "Imagery &copy; Google",
    bg: "#0a0a0b",
  },
};

// Legacy persisted value "street" maps onto the new light basemap.
export const resolveLayer = (v) => (TILE_LAYERS[v] ? v : "light");

export function MapStyleToggle({ layer, onChange, testId = "map-layer-toggle" }) {
  return (
    <div className="flex items-center gap-1 border border-industrial p-1 bg-surface flex-wrap" data-testid={testId}>
      {Object.entries(TILE_LAYERS).map(([key, cfg]) => (
        <button
          key={key}
          type="button"
          data-testid={`layer-${key}`}
          onClick={() => onChange(key)}
          className={`mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 inline-flex items-center gap-1.5 transition-colors ${layer === key ? "bg-primary-orange text-black" : "text-muted-industrial hover:text-primary-orange"}`}
        >
          <cfg.Icon className="w-3 h-3" /> {cfg.label}
        </button>
      ))}
    </div>
  );
}

export function HeadingToggle({ on, onChange, source, testId = "map-heading-toggle" }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!on)}
      title="Rotate the map so the top of the screen is the way you're facing"
      className={`border p-1.5 px-2.5 mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${on ? "bg-primary-orange text-black border-primary-orange" : "border-industrial bg-surface text-muted-industrial hover:text-primary-orange"}`}
    >
      <Compass className="w-3.5 h-3.5" />
      {on ? (source ? `Heading up · ${source}` : "Heading up · waiting") : "North up"}
    </button>
  );
}

/* Device heading from the compass, falling back to GPS course over ground.
   Also streams the viewer's own position so the map can show where they are. */
export function useDeviceHeading(enabled) {
  const [heading, setHeading] = useState(0);
  const [source, setSource] = useState(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!enabled) { setHeading(0); setSource(null); setPosition(null); return; }
    let dead = false;
    let watchId = null;
    let gotCompass = false;

    const onOrient = (e) => {
      if (dead) return;
      const raw = e.webkitCompassHeading != null
        ? e.webkitCompassHeading
        : (e.absolute && e.alpha != null ? 360 - e.alpha : null);
      if (raw == null || !Number.isFinite(raw)) return;
      gotCompass = true;
      setSource("compass");
      setHeading(Math.round(((raw % 360) + 360) % 360 / 2) * 2);
    };

    const start = async () => {
      try {
        if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
          const res = await DeviceOrientationEvent.requestPermission();
          if (res === "granted") {
            window.addEventListener("deviceorientation", onOrient, true);
          }
        } else {
          window.addEventListener("deviceorientationabsolute", onOrient, true);
          window.addEventListener("deviceorientation", onOrient, true);
        }
      } catch { /* no compass — GPS course below still works */ }

      if ("geolocation" in navigator) {
        watchId = navigator.geolocation.watchPosition(
          (p) => {
            if (dead) return;
            setPosition([p.coords.latitude, p.coords.longitude]);
            const h = p.coords.heading;
            if (!gotCompass && Number.isFinite(h) && (p.coords.speed ?? 0) > 0.5) {
              setSource("gps");
              setHeading(Math.round(((h % 360) + 360) % 360 / 2) * 2);
            }
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 2000 },
        );
      }
    };
    start();

    return () => {
      dead = true;
      window.removeEventListener("deviceorientationabsolute", onOrient, true);
      window.removeEventListener("deviceorientation", onOrient, true);
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, [enabled]);

  return { heading, source, position };
}

/* Leaflet sizes itself once at mount, so tell it to re-measure whenever the
   rotation frame grows or shrinks. */
export function InvalidateOnChange({ dep }) {
  const map = useMap();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const t = setTimeout(() => map.invalidateSize(), 220);
    return () => clearTimeout(t);
  }, [dep, map]);
  return null;
}

/* Oversizes and counter-rotates the Leaflet canvas. The rotated layer is sized
   to the container's diagonal so no corner can ever expose a gap. Children are
   the usual <MapContainer>…</MapContainer>. */
export function RotateFrame({ bearing = 0, className = "", children }) {
  const active = bearing !== 0;
  const boxRef = useRef(null);
  const [side, setSide] = useState(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      setSide(Math.ceil(Math.hypot(width, height)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const inner = active && side
    ? { position: "absolute", top: "50%", left: "50%", width: side, height: side, marginTop: -side / 2, marginLeft: -side / 2 }
    : { position: "absolute", inset: 0 };

  return (
    <div ref={boxRef} className={`relative w-full h-full overflow-hidden ${className}`}>
      <div
        className={active ? "map-rotate" : undefined}
        style={{
          ...inner,
          transform: active ? `rotate(${-bearing}deg)` : "none",
          transition: "transform 180ms linear",
          ["--map-bearing"]: `${bearing}deg`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
